import { useState, useEffect, useCallback } from "react";
import {
  fetchTransactionStats,
  fetchRecentTransactions,
  transformTransactionStatsToDailyData,
  transformTransactionsToDailyData,
  safeNumber
} from "@/services/api/services/admin-dashboard";
import type { TransactionStatsResponse, RecentTransactionsResponse, DailyTransactionData } from "@/services/api/services/admin-dashboard";

export type TransactionPeriod = "today" | "week" | "month" | "last90d";

interface UseTransactionDataReturn {
  // Stats data
  statsData: TransactionStatsResponse['data'] | null;
  statsLoading: boolean;
  statsError: string | null;

  // Recent transactions data
  recentData: RecentTransactionsResponse['data'] | null;
  recentLoading: boolean;
  recentError: string | null;

  // Chart data
  chartData: DailyTransactionData[];
  chartLoading: boolean;
  chartError: string | null;

  // Actions
  refreshStats: () => Promise<void>;
  refreshRecent: () => Promise<void>;
  refreshAll: () => Promise<void>;
  changePeriod: (period: TransactionPeriod) => void;

  // Current state
  currentPeriod: TransactionPeriod;
  lastUpdated: Date | null;
}

export function useTransactionData(
  refreshInterval: number = 30000, // 30 seconds default
  defaultPeriod: TransactionPeriod = "today",
  initialData?: DailyTransactionData[]
): UseTransactionDataReturn {
  const [currentPeriod, setCurrentPeriod] = useState<TransactionPeriod>(defaultPeriod);

  // Stats state
  const [statsData, setStatsData] = useState<TransactionStatsResponse['data'] | null>(null);
  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Recent transactions state
  const [recentData, setRecentData] = useState<RecentTransactionsResponse['data'] | null>(null);
  const [recentLoading, setRecentLoading] = useState<boolean>(false);
  const [recentError, setRecentError] = useState<string | null>(null);

  // Chart data state
  const [chartData, setChartData] = useState<DailyTransactionData[]>([]);
  const [chartLoading, setChartLoading] = useState<boolean>(false);
  const [chartError, setChartError] = useState<string | null>(null);

  // Last updated timestamp
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Update chart data whenever stats or recent data changes
  const updateChartData = useCallback(async () => {
    setChartLoading(true);
    setChartError(null);

    try {
      // PRIORITY 1: Use initialData if provided and not empty
      if (initialData && initialData.length > 0) {
        // Validate initialData structure (handle string to number conversion)
        const isValidData = initialData.every(item =>
          item.date &&
          !isNaN(safeNumber(item.payinVolume)) &&
          !isNaN(safeNumber(item.payoutVolume))
        );

        if (isValidData) {
          setChartData(initialData);
          return;
        }
      }

      // PRIORITY 2: Try to get detailed data from recent transactions
      if (recentData && recentData.transactions.length > 0) {
        const dailyData = transformTransactionsToDailyData(recentData.transactions);
        setChartData(dailyData);
        return;
      }

      // PRIORITY 3: Fallback to stats data if recent data is not available
      if (statsData) {
        const periodKey = currentPeriod === "today" ? "today" :
                         currentPeriod === "week" ? "thisWeek" :
                         currentPeriod === "month" ? "thisMonth" : "last90Days";

        const statsChartData = transformTransactionStatsToDailyData(statsData, periodKey);
        setChartData(statsChartData);
        return;
      }

      // PRIORITY 4: If no data is available, set empty array (will trigger empty state in UI)
      setChartData([]);
    } catch (error) {
      setChartError(error instanceof Error ? error.message : 'Failed to update chart data');
      setChartData([]);
    } finally {
      setChartLoading(false);
    }
  }, [statsData, recentData, currentPeriod, initialData]);

  // Fetch stats data
  const refreshStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);

    try {
      const data = await fetchTransactionStats(currentPeriod);
      setStatsData(data);
      setLastUpdated(new Date());
    } catch (error) {
      setStatsError(error instanceof Error ? error.message : 'Failed to fetch stats');
    } finally {
      setStatsLoading(false);
    }
  }, [currentPeriod]);

  // Fetch recent transactions data
  const refreshRecent = useCallback(async () => {
    setRecentLoading(true);
    setRecentError(null);

    try {
      const data = await fetchRecentTransactions(1, 50); // Get last 50 transactions
      setRecentData(data);
    } catch (error) {
      setRecentError(error instanceof Error ? error.message : 'Failed to fetch recent transactions');
    } finally {
      setRecentLoading(false);
    }
  }, []);

  // Refresh all data
  const refreshAll = useCallback(async () => {
    await Promise.all([refreshStats(), refreshRecent()]);
  }, [refreshStats, refreshRecent]);

  // Change period and refresh data
  const changePeriod = useCallback((period: TransactionPeriod) => {
    if (period !== currentPeriod) {
      setCurrentPeriod(period);
    }
  }, [currentPeriod]);

  // Initial data fetch - only if initialData not provided
  useEffect(() => {
    if (!initialData || initialData.length === 0) {
      refreshAll();
    } else {
      // Set lastUpdated even when using initialData
      setLastUpdated(new Date());
    }
  }, []); // Only run once on mount

  // Set up interval for auto-refresh
  useEffect(() => {
    if (refreshInterval <= 0) return;

    const interval = setInterval(() => {
      refreshAll();
    }, refreshInterval);

    return () => {
      clearInterval(interval);
    };
  }, [refreshInterval, refreshAll]);

  // Refresh when period changes
  useEffect(() => {
    if (currentPeriod !== defaultPeriod) { // Only refresh if it's not the initial load
      refreshStats();
    }
  }, [currentPeriod, refreshStats]);

  // Update chart data whenever stats or recent data changes
  useEffect(() => {
    updateChartData();
  }, [updateChartData]);

  return {
    // Stats data
    statsData,
    statsLoading,
    statsError,

    // Recent transactions data
    recentData,
    recentLoading,
    recentError,

    // Chart data
    chartData,
    chartLoading,
    chartError,

    // Actions
    refreshStats,
    refreshRecent,
    refreshAll,
    changePeriod,

    // Current state
    currentPeriod,
    lastUpdated
  };
}